/**
 * AgentLeadChat.js
 * Agent portal page for managing lead conversations.
 * Shows all leads assigned to the agent, with real-time chat + SMS/Email compose.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useLocation } from 'react-router-dom';

import LeadInfoPane from './LeadInfoPane';
import { useNotificationSound } from '../../utils/notificationSound';

const INS_LABELS = { aca: 'ACA Health', medicare: 'Medicare', dental: 'Dental', vision: 'Vision', life: 'Life' };
const INS_ICONS  = { aca: '🏥', medicare: '👴', dental: '🦷', vision: '👁️', life: '❤️' };
const CH_ICONS   = { chat: '💬', sms: '📱', email: '📧' };

// ── ChannelBtn — channel pill with disabled+tooltip support ───────────────────
function ChannelBtn({ ch, active, enabled, onSelect, disabledMsg }) {
  const [showTip, setShowTip] = React.useState(false);
  if (enabled) {
    return (
      <button onClick={() => onSelect(ch)} style={{ padding: '3px 10px', borderRadius: '999px', border: '1px solid', fontSize: '11px', cursor: 'pointer', fontWeight: active ? 700 : 400, background: active ? '#059669' : '#fff', borderColor: active ? '#059669' : '#d1d5db', color: active ? '#fff' : '#374151' }}>
        {CH_ICONS[ch]} {ch.toUpperCase()}
      </button>
    );
  }
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <button
        disabled
        style={{ padding: '3px 10px', borderRadius: '999px', border: '1px solid #e5e7eb', fontSize: '11px', cursor: 'not-allowed', fontWeight: 400, background: '#f3f4f6', borderColor: '#e5e7eb', color: '#9ca3af', opacity: 0.7, position: 'relative' }}
      >
        {CH_ICONS[ch]} {ch.toUpperCase()}
        <span style={{ marginLeft: '4px', fontSize: '10px' }}>🔒</span>
      </button>
      {showTip && (
        <div style={{ position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)', background: '#1f2937', color: '#fff', borderRadius: '8px', padding: '8px 12px', fontSize: '11.5px', whiteSpace: 'nowrap', maxWidth: '220px', whiteSpace: 'normal', textAlign: 'center', zIndex: 100, lineHeight: 1.4, boxShadow: '0 4px 12px rgba(0,0,0,.2)', pointerEvents: 'none' }}>
          🔒 {disabledMsg}
          <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #1f2937' }} />
        </div>
      )}
    </div>
  );
}
const EMOJI_LIST = ['👍','❤️','😂','😮','😢','👏'];

const REQUEST_TYPE_LABELS = {
  call_me:               '📞 Call Me',
  help_with_card:        '💳 I Need Help With My Card',
  update_my_info:        '✏️ I Need to Update My Info',
  received_notice:       '📬 I Received a Notice',
  upload_documents:      '📎 I Need to Upload Documents',
  review_plan:           '🔍 I Want to Review My Plan',
  policy_status_changed: '⚠️ My Policy Status Changed',
  benefit_request:       '🎁 Benefit Request',
  other:                 '💬 Other',
};

function authHeaders() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('agentToken')}` } };
}

/**
 * formatLeadName – returns the best display name for a lead.
 * Prefers the stored name (first + last) over the email address.
 * Falls back to the email only if no name is set at all.
 */
function formatLeadName(name, email) {
  if (name && name.trim() && name.trim() !== email) return name.trim();
  return email || 'Unknown';
}

/** Single-letter avatar initial from a formatted display name. */
function nameInitial(name, email) {
  const display = formatLeadName(name, email);
  return display[0].toUpperCase();
}

/**
 * fileViewUrl – convert stored S3 URL → server-proxy download URL.
 * Embeds the agent JWT so <img src> and browser-opened <a href> work
 * (browsers don't forward Authorization headers for those requests).
 * Server validates the token and 302-redirects to a presigned S3 GET URL.
 */
function fileViewUrl(fileUrl) {
  if (!fileUrl) return '#';
  const match = fileUrl.match(/lead-portal\/.+/);
  if (!match) return fileUrl;
  const tok = localStorage.getItem('agentToken') || '';
  return `/api/lead-portal/files/download?key=${encodeURIComponent(match[0])}&token=${encodeURIComponent(tok)}`;
}

function humanFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}
function fileIcon(mt) {
  if (!mt) return '📎';
  if (mt.startsWith('image/')) return '🖼️';
  if (mt.startsWith('video/')) return '🎬';
  if (mt.startsWith('audio/')) return '🎵';
  if (mt.includes('pdf'))  return '📄';
  return '📎';
}

// ─── Safe content extractor ───────────────────────────────────────────────────
// GHL inbound webhooks sometimes store content as an object {type, body} instead
// of a plain string. This normalizes it so React never gets an object to render.
function safeContent(content) {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (typeof content === 'object') {
    return content.body || content.text || content.message || content.preview || JSON.stringify(content);
  }
  return String(content);
}

// ─── Message bubble (agent view) ──────────────────────────────────────────────
function AgentMsgBubble({ msg, agentEmail, onReact, onDelete }) {
  // Use senderRole as primary signal (agent = me = right side).
  // Fall back to email comparison for older messages that might lack senderRole.
  const isMe = msg.senderRole === 'agent'
    || (agentEmail && msg.senderEmail?.toLowerCase() === agentEmail.toLowerCase());
  const [showMenu, setShowMenu] = useState(false);
  const [showReact, setShowReact] = useState(false);
  const reactCounts = Object.entries(msg.reactions || {}).filter(([, u]) => u.length > 0);

  return (
    <div style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', marginBottom: '10px', gap: '8px', alignItems: 'flex-end' }}>
      <div style={{ width: 30, height: 30, borderRadius: '50%', background: isMe ? '#059669' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, color: isMe ? '#fff' : '#374151', flexShrink: 0 }}>
        {(msg.senderName || '?')[0].toUpperCase()}
      </div>
      <div style={{ maxWidth: '70%', position: 'relative' }}>
        {!isMe && <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '3px' }}>{msg.senderName} {msg.channel !== 'chat' ? `· ${CH_ICONS[msg.channel]} ${msg.channel?.toUpperCase()}` : ''}</div>}
        <div
          onMouseEnter={() => setShowMenu(true)}
          onMouseLeave={() => { setShowMenu(false); setShowReact(false); }}
          style={{ background: isMe ? '#059669' : '#f3f4f6', color: isMe ? '#fff' : '#111827', borderRadius: isMe ? '16px 4px 16px 16px' : '4px 16px 16px 16px', padding: '9px 13px', fontSize: '14px', lineHeight: 1.5, wordBreak: 'break-word', position: 'relative' }}
        >
          {msg.deletedAt ? <em style={{ opacity: .55 }}>Message deleted</em> : (
            <>
              {msg.subject && <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '13px' }}>📧 {msg.subject}</div>}
              {msg.fileUrl && (() => {
                const vUrl = fileViewUrl(msg.fileUrl);
                if (msg.type === 'image') return (
                  <div style={{ marginBottom: msg.content ? '6px' : 0 }}>
                    <a href={vUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                      <img src={vUrl} alt={msg.fileName || 'Image'} style={{ maxWidth: '220px', borderRadius: '8px', display: 'block' }}
                        onError={e => { e.currentTarget.style.display = 'none'; }} />
                    </a>
                    <a href={vUrl} target="_blank" rel="noreferrer"
                      style={{ display: 'block', fontSize: '11px', color: isMe ? 'rgba(255,255,255,.7)' : '#6b7280', textDecoration: 'none', marginTop: '3px' }}>
                      🖼️ {msg.fileName || 'View Image'}
                    </a>
                  </div>
                );
                if (msg.type === 'video') return (
                  <a href={vUrl} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: isMe ? 'rgba(255,255,255,.15)' : '#e5e7eb', borderRadius: '8px', marginBottom: msg.content ? '6px' : 0, textDecoration: 'none', color: 'inherit', fontSize: '13px' }}>
                    <span style={{ fontSize: '22px' }}>🎬</span>
                    <div><div style={{ fontWeight: 600 }}>{msg.fileName || 'Video'}</div><div style={{ fontSize: '11px', opacity: .7 }}>{humanFileSize(msg.fileSize)}</div></div>
                  </a>
                );
                if (msg.type === 'audio') return (
                  <a href={vUrl} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: isMe ? 'rgba(255,255,255,.15)' : '#e5e7eb', borderRadius: '8px', marginBottom: msg.content ? '6px' : 0, textDecoration: 'none', color: 'inherit', fontSize: '13px' }}>
                    <span style={{ fontSize: '22px' }}>🎵</span>
                    <div><div style={{ fontWeight: 600 }}>{msg.fileName || 'Audio'}</div><div style={{ fontSize: '11px', opacity: .7 }}>{humanFileSize(msg.fileSize)}</div></div>
                  </a>
                );
                // Generic file (PDF, DOC, etc.)
                return (
                  <a href={vUrl} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: isMe ? 'rgba(255,255,255,.15)' : '#e5e7eb', borderRadius: '8px', marginBottom: msg.content ? '6px' : 0, textDecoration: 'none', color: 'inherit', fontSize: '13px' }}>
                    <span style={{ fontSize: '22px' }}>{fileIcon(msg.mimeType)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, maxWidth: '160px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{msg.fileName || 'File'}</div>
                      <div style={{ fontSize: '11px', opacity: .7 }}>{humanFileSize(msg.fileSize)}</div>
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: 700, opacity: .6 }}>↓</span>
                  </a>
                );
              })()}
              {safeContent(msg.content) && <span>{safeContent(msg.content)}</span>}
            </>
          )}
          {showMenu && !msg.deletedAt && (
            <div style={{ position: 'absolute', top: '-30px', right: isMe ? 0 : 'unset', left: isMe ? 'unset' : 0, background: '#1f2937', borderRadius: '8px', display: 'flex', padding: '4px', gap: '2px', zIndex: 10 }}>
              <button onClick={() => setShowReact(r => !r)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#fff', padding: '2px 4px' }}>😊</button>
              {isMe && <button onClick={() => onDelete(msg.messageId)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#fff', padding: '2px 4px' }}>🗑️</button>}
            </div>
          )}
          {showReact && (
            <div style={{ position: 'absolute', top: '-62px', right: isMe ? 0 : 'unset', left: isMe ? 'unset' : 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', display: 'flex', gap: '4px', padding: '6px', zIndex: 20, boxShadow: '0 4px 16px rgba(0,0,0,.1)' }}>
              {EMOJI_LIST.map(em => <button key={em} onClick={() => { onReact(msg.messageId, em); setShowReact(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}>{em}</button>)}
            </div>
          )}
        </div>
        {reactCounts.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '3px', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
            {reactCounts.map(([em, users]) => (
              <button key={em} onClick={() => onReact(msg.messageId, em)} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '999px', padding: '1px 7px', fontSize: '12px', cursor: 'pointer' }}>
                {em} <span style={{ fontSize: '10px', color: '#6b7280' }}>{users.length}</span>
              </button>
            ))}
          </div>
        )}
        <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '3px', textAlign: isMe ? 'right' : 'left' }}>
          {isMe && msg.channel !== 'chat' && <span style={{ marginRight: 4 }}>{CH_ICONS[msg.channel]} {msg.channel?.toUpperCase()} · </span>}
          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {isMe && msg.status === 'read' && <span style={{ marginLeft: '4px', color: '#22c55e' }}>✓✓</span>}
        </div>
      </div>
    </div>
  );
}

// ─── Chat pane ────────────────────────────────────────────────────────────────
function AgentChatPane({ conv, agentEmail, agentName, channelConfig = {} }) {
  const playSound = useNotificationSound();
  const smsEnabled   = channelConfig.smsEnabled   !== false;
  const emailEnabled = channelConfig.emailEnabled !== false;

  const [messages, setMessages]   = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [text, setText]           = useState('');
  const [sending, setSending]     = useState(false);
  const [channel, setChannel]     = useState('chat');

  // If the current channel is disabled by admin, revert to chat
  React.useEffect(() => {
    if (channel === 'sms'   && !smsEnabled)   setChannel('chat');
    if (channel === 'email' && !emailEnabled) setChannel('chat');
  }, [smsEnabled, emailEnabled, channel]);
  const [emailSubj, setEmailSubj] = useState('');
  const [leadTyping, setLeadTyping] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const socketRef  = useRef(null);
  const bottomRef  = useRef(null);
  const fileRef    = useRef(null);
  const tTimer     = useRef(null);
  const [isTyping, setIsTyping]   = useState(false);
  const [pane, setPane]           = useState('chat'); // 'chat' | 'requests' | 'updates'
  const [requests, setRequests]   = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqNotes, setReqNotes]   = useState({}); // { requestId: noteText }

  // ── Updates pane state ───────────────────────────────────────────────────
  const [updates, setUpdates]       = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [updTitle, setUpdTitle]     = useState('');
  const [updBody, setUpdBody]       = useState('');
  const [sendingUpd, setSendingUpd] = useState(false);
  const [updSuccess, setUpdSuccess] = useState('');

  const token = localStorage.getItem('agentToken');

  useEffect(() => {
    if (!conv?.conversationId) return;
    axios.get(`/api/lead-portal/conversations/${conv.conversationId}/messages`, authHeaders())
      .then(r => { setMessages([...(r.data.messages || [])].reverse()); setNextCursor(r.data.nextCursor); })
      .catch(console.error);
  }, [conv?.conversationId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  useEffect(() => {
    if (!conv?.conversationId || !token) return;
    const socket = io('/chat', { auth: { token }, transports: ['websocket'] });
    socketRef.current = socket;
    socket.on('connect', () => socket.emit('join:conversation', { conversationId: conv.conversationId }));
    socket.on('message:new', ({ message }) => {
      // Play sound for inbound messages from leads
      if (message.senderRole === 'lead' || message.senderEmail !== agentEmail) playSound();
      setMessages(p => p.find(m => m.messageId === message.messageId) ? p : [...p, message]);
      if (message.senderEmail !== agentEmail) socket.emit('message:read', { conversationId: conv.conversationId, messageId: message.messageId });
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    });
    socket.on('message:deleted', ({ messageId }) => setMessages(p => p.map(m => m.messageId === messageId ? { ...m, deletedAt: new Date().toISOString(), content: 'Message deleted' } : m)));
    socket.on('message:reaction', ({ messageId, reactions }) => setMessages(p => p.map(m => m.messageId === messageId ? { ...m, reactions } : m)));
    socket.on('message:readAll', () => setMessages(p => p.map(m => ({ ...m, status: m.senderEmail === agentEmail ? 'read' : m.status }))));
    socket.on('typing:start', ({ senderEmail }) => { if (senderEmail !== agentEmail) setLeadTyping(true); });
    socket.on('typing:stop',  ({ senderEmail }) => { if (senderEmail !== agentEmail) setLeadTyping(false); });
    return () => { socket.emit('leave:conversation', { conversationId: conv.conversationId }); socket.disconnect(); };
  }, [conv?.conversationId, token, agentEmail]);

  const handleType = (val) => {
    setText(val);
    if (!isTyping) { setIsTyping(true); socketRef.current?.emit('typing:start', { conversationId: conv.conversationId }); }
    clearTimeout(tTimer.current);
    tTimer.current = setTimeout(() => { setIsTyping(false); socketRef.current?.emit('typing:stop', { conversationId: conv.conversationId }); }, 1500);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (channel === 'email' && !emailSubj.trim()) return;
    setSending(true);
    try {
      let res;
      if (channel === 'sms')    res = await axios.post(`/api/lead-portal/conversations/${conv.conversationId}/sms`,   { content: text }, authHeaders());
      else if (channel === 'email') res = await axios.post(`/api/lead-portal/conversations/${conv.conversationId}/email`, { subject: emailSubj, content: text, html: `<p>${text}</p>` }, authHeaders());
      else                      res = await axios.post(`/api/lead-portal/conversations/${conv.conversationId}/messages`, { content: text, type: 'text' }, authHeaders());

      // Optimistically add the sent message from the API response so the UI
      // updates immediately even when socket is unavailable (e.g. WebSocket
      // blocked by proxy — socket handles it for the other party).
      const newMsg = res?.data?.message;
      if (newMsg) {
        setMessages(prev =>
          prev.find(m => m.messageId === newMsg.messageId) ? prev : [...prev, newMsg]
        );
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      }

      setText(''); setEmailSubj('');
      socketRef.current?.emit('typing:stop', { conversationId: conv.conversationId });
    } catch (err) {
      console.error(err);
      alert('Failed to send message. Please try again.');
    } finally { setSending(false); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; e.target.value = ''; setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(
        `/api/lead-portal/conversations/${conv.conversationId}/upload`,
        fd,
        {
          headers: { ...authHeaders().headers },
          onUploadProgress: (ev) => setUploadPct(Math.round(ev.loaded / ev.total * 100))
        }
      );
      setUploadPct(null);
      // Optimistically add to messages (socket also fires — dedup by messageId)
      if (data.message) {
        setMessages(prev => prev.find(m => m.messageId === data.message.messageId) ? prev : [...prev, data.message]);
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      }
    } catch (err) { setUploadPct(null); console.error(err); alert('Upload failed. Please try again.'); }
  };

  const handleReact  = (msgId, emoji) => axios.post(`/api/lead-portal/conversations/${conv.conversationId}/messages/${msgId}/react`, { emoji }, authHeaders()).catch(console.error);
  const handleDelete = (msgId) => { if (window.confirm('Delete message?')) axios.delete(`/api/lead-portal/conversations/${conv.conversationId}/messages/${msgId}`, authHeaders()).catch(console.error); };

  const loadMore = async () => {
    if (!nextCursor) return;
    const r = await axios.get(`/api/lead-portal/conversations/${conv.conversationId}/messages?before=${encodeURIComponent(nextCursor)}`, authHeaders());
    setMessages(p => [...[...(r.data.messages || [])].reverse(), ...p]);
    setNextCursor(r.data.nextCursor);
  };

  // ── Fetch lead requests whenever the Requests tab is opened ──────────────
  const fetchRequests = () => {
    if (!conv?.leadEmail) return;
    setReqLoading(true);
    axios.get(`/api/lead-portal/agent/lead-requests?leadEmail=${encodeURIComponent(conv.leadEmail)}`, authHeaders())
      .then(r => setRequests(r.data.requests || []))
      .catch(console.error)
      .finally(() => setReqLoading(false));
  };

  useEffect(() => {
    if (pane === 'requests') fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pane, conv?.leadEmail]);

  // ── Fetch updates for this lead when Updates tab opens ───────────────────
  const fetchUpdates = () => {
    if (!conv?.leadEmail) return;
    setUpdatesLoading(true);
    axios.get(`/api/lead-portal/agent/leads/${encodeURIComponent(conv.leadEmail)}/updates`, authHeaders())
      .then(r => setUpdates(r.data.updates || []))
      .catch(console.error)
      .finally(() => setUpdatesLoading(false));
  };

  useEffect(() => {
    if (pane === 'updates') fetchUpdates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pane, conv?.leadEmail]);

  const sendUpdate = async () => {
    if (!updTitle.trim() || !updBody.trim()) return;
    setSendingUpd(true);
    setUpdSuccess('');
    try {
      const insType = conv.insuranceType || 'aca';
      await axios.post(`/api/lead-portal/agent/leads/${encodeURIComponent(conv.leadEmail)}/updates`,
        { title: updTitle, body: updBody, insuranceType: insType },
        authHeaders()
      );
      setUpdTitle('');
      setUpdBody('');
      setUpdSuccess('✅ Update sent! Lead will receive SMS + Email notification.');
      fetchUpdates();
      setTimeout(() => setUpdSuccess(''), 5000);
    } catch (err) {
      console.error(err);
      alert('Failed to send update. Please try again.');
    } finally { setSendingUpd(false); }
  };

  // ── Reset pane to 'chat' when switching to a different lead ──────────────
  useEffect(() => { setPane('chat'); setRequests([]); setUpdates([]); setUpdTitle(''); setUpdBody(''); setUpdSuccess(''); }, [conv?.leadEmail, conv?.insuranceType]);

  const resolveRequest = async (requestId, status) => {
    const agentNote = reqNotes[requestId] || '';
    try {
      await axios.patch(`/api/lead-portal/agent/requests/${requestId}`, { status, agentNote }, authHeaders());
      setRequests(p => p.map(r => r.requestId === requestId ? { ...r, status, agentNote } : r));
    } catch (err) {
      console.error(err);
      alert('Failed to update request.');
    }
  };

  const openCount = requests.filter(r => r.status !== 'resolved').length;

  /* ── Status badge helper ── */
  const statusBadge = (status) => {
    const cfg = {
      open:        { bg: '#fef3c7', color: '#d97706', label: 'Open' },
      in_progress: { bg: '#dbeafe', color: '#1d4ed8', label: 'In Progress' },
      resolved:    { bg: '#d1fae5', color: '#065f46', label: 'Resolved' },
    };
    const c = cfg[status] || cfg.open;
    return <span style={{ background: c.bg, color: c.color, borderRadius: '999px', padding: '2px 10px', fontSize: '11px', fontWeight: 700 }}>{c.label}</span>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>

      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '14px' }}>
          {nameInitial(conv.leadName, conv.leadEmail)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '14px', color: '#111827' }}>{formatLeadName(conv.leadName, conv.leadEmail)}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>{conv.leadEmail} · {INS_ICONS[conv.insuranceType]} {INS_LABELS[conv.insuranceType] || conv.insuranceType}</div>
        </div>
      </div>

      {/* Tab bar: Chat | Lead Info | Requests | Updates | 🕓 (logs icon) */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }}>
        <div style={{ display: 'flex', flex: 1 }}>
          {[
            { id: 'chat',      label: '💬 Chat' },
            { id: 'lead-info', label: '🗂 Lead Info' },
            { id: 'requests',  label: `📋 Requests${openCount > 0 ? ` (${openCount})` : ''}` },
            { id: 'updates',   label: '📢 Updates' },
          ].map(t => (
            <button key={t.id} onClick={() => setPane(t.id)} style={{
              padding: '9px 18px', fontSize: '13px', fontWeight: pane === t.id ? 700 : 400,
              color: pane === t.id ? '#059669' : '#6b7280',
              borderBottom: pane === t.id ? '2px solid #059669' : '2px solid transparent',
              background: 'none', border: 'none', borderTopLeftRadius: 0, borderTopRightRadius: 0,
              cursor: 'pointer',
            }}>{t.label}</button>
          ))}
        </div>
        {/* Logs icon — right-aligned, icon only */}
        <button
          onClick={() => setPane('lead-logs')}
          title="Edit History"
          style={{
            padding: '9px 14px', fontSize: '16px',
            color: pane === 'lead-logs' ? '#059669' : '#9ca3af',
            borderBottom: pane === 'lead-logs' ? '2px solid #059669' : '2px solid transparent',
            background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
          }}>🕓</button>
      </div>

      {/* ══ CHAT PANE ══ */}
      {pane === 'chat' && (
        <>
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column' }}>
            {nextCursor && <button onClick={loadMore} style={{ alignSelf: 'center', background: '#f3f4f6', border: 'none', borderRadius: '999px', padding: '5px 14px', cursor: 'pointer', fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>↑ Load older</button>}
            {messages.length === 0 && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>💬</div>
                <div style={{ fontSize: '14px' }}>No messages yet. Start the conversation!</div>
              </div>
            )}
            {messages.map(msg => <AgentMsgBubble key={msg.messageId} msg={msg} agentEmail={agentEmail} onReact={handleReact} onDelete={handleDelete} />)}
            {leadTyping && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', fontWeight: 700 }}>
                  {nameInitial(conv.leadName, conv.leadEmail)}
                </div>
                <div style={{ background: '#f3f4f6', borderRadius: '14px', padding: '7px 12px', display: 'flex', gap: '3px' }}>
                  {[0,1,2].map(i => <span key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: '#9ca3af', display: 'inline-block', animation: `blink 1.4s ${i*0.2}s infinite` }} />)}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {uploadPct !== null && (
            <div style={{ padding: '0 14px 6px' }}>
              <div style={{ height: '3px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#059669', width: `${uploadPct}%`, transition: 'width .2s' }} />
              </div>
            </div>
          )}

          <div style={{ borderTop: '1px solid #e5e7eb', padding: '10px 12px', background: '#f9fafb' }}>
            <div style={{ display: 'flex', gap: '5px', marginBottom: '7px' }}>
              {/* Chat — always enabled */}
              <button onClick={() => setChannel('chat')} style={{ padding: '3px 10px', borderRadius: '999px', border: '1px solid', fontSize: '11px', cursor: 'pointer', fontWeight: channel === 'chat' ? 700 : 400, background: channel === 'chat' ? '#059669' : '#fff', borderColor: channel === 'chat' ? '#059669' : '#d1d5db', color: channel === 'chat' ? '#fff' : '#374151' }}>
                {CH_ICONS.chat} CHAT
              </button>
              {/* SMS — disabled if admin turned off */}
              <ChannelBtn ch="sms" active={channel === 'sms'} enabled={smsEnabled} onSelect={setChannel} disabledMsg="SMS has been disabled by your admin. Contact your admin to enable it." />
              {/* Email — disabled if admin turned off */}
              <ChannelBtn ch="email" active={channel === 'email'} enabled={emailEnabled} onSelect={setChannel} disabledMsg="Email has been disabled by your admin. Contact your admin to enable it." />
            </div>
            {channel === 'email' && (
              <input value={emailSubj} onChange={e => setEmailSubj(e.target.value)} placeholder="Subject" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '6px', padding: '7px 10px', marginBottom: '7px', fontSize: '13px', boxSizing: 'border-box' }} />
            )}
            <form onSubmit={send} style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
              <button type="button" onClick={() => fileRef.current?.click()} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '7px 9px', cursor: 'pointer', fontSize: '15px', flexShrink: 0 }}>📎</button>
              <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={handleFile} />
              <textarea value={text} onChange={e => handleType(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); } }}
                placeholder={channel === 'sms' ? 'Type SMS…' : channel === 'email' ? 'Email body…' : 'Message… (Enter to send)'}
                rows={1} style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: '6px', padding: '8px 10px', fontSize: '14px', resize: 'none', outline: 'none', fontFamily: 'inherit' }}
              />
              <button type="submit" disabled={sending || !text.trim()} style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', flexShrink: 0, opacity: sending ? .6 : 1 }}>
                {sending ? '…' : '➤'}
              </button>
            </form>
          </div>
        </>
      )}

      {/* ══ UPDATES PANE ══ */}
      {pane === 'updates' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', background: '#f9fafb', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Compose box */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
            <div style={{ fontWeight: 700, fontSize: '14px', color: '#111827', marginBottom: '12px' }}>
              📢 Send Update to {formatLeadName(conv.leadName, conv.leadEmail)}
            </div>
            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', lineHeight: 1.5 }}>
              The lead will see this card in their 🔔 Updates tab and receive an SMS + Email notification automatically.
            </p>

            <input
              placeholder="Title — e.g. 'Your application has been submitted ✅'"
              value={updTitle}
              onChange={e => setUpdTitle(e.target.value)}
              style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '10px', fontFamily: 'inherit', fontWeight: 600 }}
            />
            <textarea
              placeholder="Body — detailed message to the lead…"
              value={updBody}
              onChange={e => setUpdBody(e.target.value)}
              rows={4}
              style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', marginBottom: '12px' }}
            />

            {updSuccess && (
              <div style={{ background: '#d1fae5', color: '#065f46', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px', fontWeight: 600 }}>
                {updSuccess}
              </div>
            )}

            <button
              onClick={sendUpdate}
              disabled={sendingUpd || !updTitle.trim() || !updBody.trim()}
              style={{ background: sendingUpd ? '#9ca3af' : '#1a56db', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: 600, cursor: sendingUpd ? 'default' : 'pointer', width: '100%' }}
            >
              {sendingUpd ? 'Sending…' : '📤 Send Update + Notify Lead'}
            </button>
          </div>

          {/* Sent updates history */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#374151' }}>Sent Updates</div>
              <button onClick={fetchUpdates} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', color: '#6b7280' }}>↻ Refresh</button>
            </div>

            {updatesLoading && <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: '13px' }}>Loading…</div>}

            {!updatesLoading && updates.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: '#9ca3af', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>No updates sent yet</div>
              </div>
            )}

            {updates.map(u => (
              <div key={u.updateId} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px 14px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#111827', flex: 1 }}>{u.title}</span>
                  {u.isBroadcast && <span style={{ background: '#eff6ff', color: '#1a56db', borderRadius: '999px', padding: '1px 8px', fontSize: '10px', fontWeight: 700, marginLeft: 8, flexShrink: 0 }}>BROADCAST</span>}
                </div>
                <p style={{ fontSize: '12px', color: '#374151', lineHeight: 1.5, margin: '0 0 6px' }}>{u.body}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9ca3af' }}>
                  <span>{u.insuranceType?.toUpperCase()}</span>
                  <span>{new Date(u.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ REQUESTS PANE ══ */}
      {pane === 'requests' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', background: '#f9fafb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>
              Lead Requests
              {openCount > 0 && <span style={{ marginLeft: 8, background: '#fef3c7', color: '#d97706', borderRadius: '999px', padding: '1px 8px', fontSize: '11px' }}>{openCount} open</span>}
            </div>
            <button onClick={fetchRequests} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer', color: '#6b7280' }}>↻ Refresh</button>
          </div>

          {reqLoading && <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af' }}>Loading requests…</div>}

          {!reqLoading && requests.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: '#9ca3af' }}>
              <div style={{ fontSize: '36px', marginBottom: '10px' }}>📋</div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>No requests yet</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>Requests from this lead will appear here.</div>
            </div>
          )}

          {requests.map(req => (
            <div key={req.requestId} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', marginBottom: '10px' }}>
              {/* Type + status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827', flex: 1 }}>
                  {REQUEST_TYPE_LABELS[req.type] || req.type}
                </div>
                {statusBadge(req.status)}
              </div>

              {/* Lead note */}
              {req.note && (
                <div style={{ fontSize: '13px', color: '#374151', background: '#f3f4f6', borderRadius: '6px', padding: '8px 10px', marginBottom: '10px', lineHeight: 1.5 }}>
                  <strong>Lead note:</strong> {req.note}
                </div>
              )}

              {/* Agent note */}
              {req.agentNote && req.status === 'resolved' ? (
                <div style={{ fontSize: '12px', color: '#059669', background: '#d1fae5', borderRadius: '6px', padding: '7px 10px', marginBottom: '10px' }}>
                  ✅ <strong>Your response:</strong> {req.agentNote}
                </div>
              ) : (
                <textarea
                  placeholder="Add a response note (optional)…"
                  value={reqNotes[req.requestId] || req.agentNote || ''}
                  onChange={e => setReqNotes(p => ({ ...p, [req.requestId]: e.target.value }))}
                  rows={2}
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', marginBottom: '10px', fontFamily: 'inherit' }}
                />
              )}

              {/* Date + actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                  {new Date(req.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {req.insuranceType && ` · ${req.insuranceType.toUpperCase()}`}
                </span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {req.status !== 'in_progress' && req.status !== 'resolved' && (
                    <button onClick={() => resolveRequest(req.requestId, 'in_progress')}
                      style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                      In Progress
                    </button>
                  )}
                  {req.status !== 'resolved' ? (
                    <button onClick={() => resolveRequest(req.requestId, 'resolved')}
                      style={{ background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                      ✓ Resolve
                    </button>
                  ) : (
                    <button onClick={() => resolveRequest(req.requestId, 'open')}
                      style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>
                      Re-open
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ══ LEAD INFO PANE ══ */}
      {pane === 'lead-info' && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <LeadInfoPane leadEmail={conv.leadEmail} insuranceType={conv.insuranceType} />
        </div>
      )}

      {pane === 'lead-logs' && (
        <LeadInfoLogs leadEmail={conv.leadEmail} insuranceType={conv.insuranceType} />
      )}

    </div>
  );
}

// ─── Lead Info Logs pane ──────────────────────────────────────────────────────
function LeadInfoLogs({ leadEmail, insuranceType }) {
  const [logs, setLogs]     = React.useState(null); // null = loading
  const [error, setError]   = React.useState(null);
  const insType = (insuranceType || 'aca').toLowerCase();

  React.useEffect(() => {
    if (!leadEmail) return;
    setLogs(null); setError(null);
    axios.get(
      `/api/agent/lead-info/${encodeURIComponent(leadEmail)}/logs?insuranceType=${insType}`,
      authHeaders()
    ).then(r => setLogs(r.data.logs || []))
     .catch(() => setError('Failed to load logs'));
  }, [leadEmail, insType]);

  const TYPE_META = {
    edit:   { icon: '✏️', color: '#d97706', bg: '#fffbeb', label: 'Edited' },
    add:    { icon: '➕', color: '#059669', bg: '#f0fdf4', label: 'Added'  },
    delete: { icon: '🗑️', color: '#ef4444', bg: '#fef2f2', label: 'Removed'},
  };

  const fmt = ts => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '14px' }}>
        Edit History
      </div>

      {logs === null && !error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#9ca3af', fontSize: '13px' }}>
          <div style={{ width: 18, height: 18, border: '2px solid #e5e7eb', borderTopColor: '#059669', borderRadius: '50%', animation: 'li-spin 0.7s linear infinite' }} />
          Loading…
        </div>
      )}
      {error && <div style={{ color: '#ef4444', fontSize: '13px' }}>{error}</div>}
      {logs !== null && logs.length === 0 && (
        <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', paddingTop: '40px' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📋</div>
          No edits recorded yet.
        </div>
      )}

      {logs !== null && logs.map((log, i) => {
        const meta = TYPE_META[log.type] || TYPE_META.edit;
        return (
          <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px 14px', marginBottom: '10px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px' }}>{meta.icon}</span>
              <span style={{ fontSize: '11px', fontWeight: 700, background: meta.bg, color: meta.color, borderRadius: '999px', padding: '1px 8px' }}>
                {meta.label}
              </span>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151', flex: 1 }}>{log.fieldLabel}</span>
              <span style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0 }}>{fmt(log.ts)}</span>
            </div>
            {/* Old → New */}
            {log.type !== 'add' && log.oldValue !== '' && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '4px' }}>
                <span style={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', width: '28px', flexShrink: 0, paddingTop: '1px' }}>was</span>
                <span style={{ fontSize: '12px', color: '#6b7280', background: '#f9fafb', borderRadius: '5px', padding: '2px 8px', wordBreak: 'break-word', textDecoration: log.type === 'delete' ? 'line-through' : 'none' }}>
                  {log.oldValue || '—'}
                </span>
              </div>
            )}
            {log.type !== 'delete' && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <span style={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', width: '28px', flexShrink: 0, paddingTop: '1px' }}>now</span>
                <span style={{ fontSize: '12px', color: '#111827', background: '#f0fdf4', borderRadius: '5px', padding: '2px 8px', wordBreak: 'break-word', fontWeight: 500 }}>
                  {log.newValue || '—'}
                </span>
              </div>
            )}
            {/* Agent */}
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#9ca3af' }}>by {log.agentEmail}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Lead list panel ──────────────────────────────────────────────────────────
export default function AgentLeadChat() {
  const [leads, setLeads]          = useState([]);
  const [loading, setLoading]      = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedIns,  setSelectedIns]  = useState(null);
  const [search, setSearch]        = useState('');

  const location   = useLocation();
  const autoOpenEmail = location.state?.openEmail || null;

  const agentToken = localStorage.getItem('agentToken');
  const stored = JSON.parse(localStorage.getItem('agentUser') || '{}');
  const agentEmail = stored.email || '';
  const agentName  = stored.name  || agentEmail;

  const [channelConfig, setChannelConfig] = useState({ smsEnabled: true, emailEnabled: true });

  useEffect(() => {
    if (!agentToken) return;
    axios.get('/api/lead-portal/agent/leads', authHeaders())
      .then(r => { setLeads(r.data.leads || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
    // Fetch both global channel config AND per-agent config, merge (most restrictive wins)
    Promise.all([
      axios.get('/api/agent/channel-config', authHeaders()).catch(() => ({ data: {} })),
      axios.get(`/api/agent/profile`, authHeaders()).catch(() => ({ data: {} })),
    ]).then(([globalRes, profileRes]) => {
      const global = globalRes.data || {};
      const perAgent = profileRes.data?.channelConfig || {};
      // Both must be enabled for the channel to be available
      setChannelConfig({
        smsEnabled:   global.smsEnabled !== false && perAgent.smsEnabled !== false,
        emailEnabled: global.emailEnabled !== false && perAgent.emailEnabled !== false,
      });
    });
  }, [agentToken]);

  // ⭐ Auto-select a lead when navigated here from Opportunities table
  useEffect(() => {
    if (!autoOpenEmail || loading || leads.length === 0) return;
    const match = leads.find(l => l.leadEmail?.toLowerCase() === autoOpenEmail.toLowerCase());
    if (match && match.insurances?.length > 0) {
      // Pick the most recent insurance (last in array) or first one
      const ins = match.insurances[match.insurances.length - 1];
      selectLead(match, ins.insuranceType);
    }
  }, [autoOpenEmail, loading, leads]);

  const filteredLeads = leads.filter(l =>
    !search || l.leadName?.toLowerCase().includes(search.toLowerCase()) || l.leadEmail?.toLowerCase().includes(search.toLowerCase())
  );

  /* ── Clear agentUnread badge in local state when agent opens a conversation ── */
  const selectLead = (lead, insType) => {
    setSelectedLead(lead);
    setSelectedIns(insType);
    // Immediately zero out the badge in local UI (no server call needed here —
    // the socket already fires message:read events as messages arrive in-pane)
    setLeads(prev => prev.map(l =>
      l.leadEmail === lead.leadEmail
        ? { ...l, insurances: l.insurances.map(i => i.insuranceType === insType ? { ...i, agentUnread: 0 } : i) }
        : l
    ));
  };

  const activeConv = selectedLead && selectedIns
    ? { conversationId: selectedLead.insurances.find(i => i.insuranceType === selectedIns)?.conversationId, leadEmail: selectedLead.leadEmail, leadName: selectedLead.leadName, insuranceType: selectedIns }
    : null;

  const totalUnread = leads.reduce((s, l) => s + l.insurances.reduce((ss, i) => ss + (i.agentUnread || 0), 0), 0);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 80px)', background: '#f3f4f6', gap: '0' }}>
      {/* Left: leads list */}
      <div style={{ width: '300px', flexShrink: 0, background: '#fff', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#111827' }}>My Leads</h2>
            {totalUnread > 0 && <span style={{ background: '#ef4444', color: '#fff', borderRadius: '999px', padding: '2px 8px', fontSize: '12px', fontWeight: 700 }}>{totalUnread}</span>}
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads…" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '7px', padding: '8px 12px', fontSize: '13px', boxSizing: 'border-box' }} />
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>Loading leads…</div>}
          {!loading && filteredLeads.length === 0 && (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ fontSize: '36px', marginBottom: '10px' }}>👥</div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>No leads yet</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>Leads will appear here when distributed.</div>
            </div>
          )}
          {filteredLeads.map(lead => {
            const totalU = lead.insurances.reduce((s, i) => s + (i.agentUnread || 0), 0);
            const isSelected = selectedLead?.leadEmail === lead.leadEmail;
            return (
              <div key={lead.leadEmail} style={{ borderBottom: '1px solid #f3f4f6' }}>
                {/* Lead header */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', gap: '10px', cursor: 'pointer', background: isSelected ? '#f0fdf4' : 'none' }}
                  onClick={() => selectLead(lead, lead.insurances[0]?.insuranceType)}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                    {nameInitial(lead.leadName, lead.leadEmail)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: totalU > 0 ? 700 : 500, fontSize: '14px', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatLeadName(lead.leadName, lead.leadEmail)}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {lead.insurances.map(i => INS_ICONS[i.insuranceType] || '📋').join(' ')} {lead.insurances.map(i => INS_LABELS[i.insuranceType] || i.insuranceType).join(', ')}
                    </div>
                  </div>
                  {totalU > 0 && <span style={{ background: '#ef4444', color: '#fff', borderRadius: '999px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>{totalU}</span>}
                </div>
                {/* Insurance sub-tabs (shown when lead selected) */}
                {isSelected && lead.insurances.length > 1 && (
                  <div style={{ display: 'flex', gap: '4px', padding: '0 14px 8px' }}>
                    {lead.insurances.map(ins => (
                      <button key={ins.insuranceType} onClick={() => selectLead(lead, ins.insuranceType)} style={{ padding: '3px 10px', borderRadius: '999px', border: '1px solid', fontSize: '11px', cursor: 'pointer', background: selectedIns === ins.insuranceType ? '#059669' : '#f3f4f6', borderColor: selectedIns === ins.insuranceType ? '#059669' : '#d1d5db', color: selectedIns === ins.insuranceType ? '#fff' : '#374151', position: 'relative' }}>
                        {INS_ICONS[ins.insuranceType]} {(INS_LABELS[ins.insuranceType] || ins.insuranceType).split(' ')[0]}
                        {ins.agentUnread > 0 && <span style={{ marginLeft: '3px', background: '#ef4444', color: '#fff', borderRadius: '999px', padding: '0 4px', fontSize: '10px' }}>{ins.agentUnread}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: chat pane */}
      <div style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {activeConv?.conversationId ? (
          <AgentChatPane conv={activeConv} agentEmail={agentEmail} agentName={agentName} channelConfig={channelConfig} />
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>💬</div>
            <div style={{ fontSize: '18px', fontWeight: 600, color: '#374151' }}>Select a Lead</div>
            <div style={{ fontSize: '14px', marginTop: '6px' }}>Choose a lead from the left to start chatting.</div>
          </div>
        )}
      </div>

      <style>{`@keyframes blink { 0%, 80%, 100% { opacity: 0.2; } 40% { opacity: 1; } }`}</style>
    </div>
  );
}
